import { useTranslation } from '@/contexts/i18n-context';
import { useOutsideClick } from '@/hooks/use-outside-click';
import { useState, useRef } from 'react';
import { Button } from '../button';
import { DownloadTreatmentPlanButton } from './download-treatment-plan-button';
import styles from './save-and-share-button.module.css';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Sheet } from '../containers/sheet';
import { DownloadReceiptButton } from './download-receipt-button';
import { DownloadReferralLetterButton } from './download-referral-letter';
import { EncounterResolution } from '@/api/api.schemas';

const translationBaseNav = 'visit.treatmentPlan.navigationButtons';

interface SaveAndShareButtonProps {
  encounterId: number;
  className?: string;
  encounterResolution: EncounterResolution;
  hasReferral: boolean;
}

export function SaveAndShareButton({
  encounterId,
  className,
  encounterResolution,
  hasReferral,
}: SaveAndShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, () => setIsOpen(false));
  const isMobile = useMediaQuery('--screen-md');

  const downloadButtons = (
    <DownloadButtons
      encounterId={encounterId}
      className={isMobile ? styles.sheetContent : styles.floatingContent}
      encounterResolution={encounterResolution}
      hasReferral={hasReferral}
    />
  );

  return (
    <div ref={ref} className={styles.saveAndShareButtonContainer}>
      <Button
        variant="link"
        className={className}
        leftIcon="description"
        rightIcon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        onClick={() => setIsOpen(!isOpen)}
      >
        {t(`${translationBaseNav}.saveAndShare`)}
      </Button>

      {isMobile ? (
        <Sheet
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          titleClassName={styles.sheetTitle}
          title={t(`${translationBaseNav}.saveAndShare`)}
        >
          {downloadButtons}
        </Sheet>
      ) : isOpen ? (
        downloadButtons
      ) : null}
    </div>
  );
}

function DownloadButtons({
  encounterId,
  className,
  encounterResolution,
  hasReferral,
}: SaveAndShareButtonProps) {
  const { t } = useTranslation();

  const isTriagedOut = encounterResolution === EncounterResolution.TriageOut;
  const isTreated = encounterResolution === EncounterResolution.Completed;

  return (
    <div className={className}>
      {isTreated && (
        <>
          <DownloadReceiptButton encounterId={encounterId} fileName="receipt.pdf">
            {t(`${translationBaseNav}.downloadReceipt`)}
          </DownloadReceiptButton>
          <DownloadTreatmentPlanButton encounterId={encounterId} fileName="treatment-plan.pdf">
            {t(`${translationBaseNav}.downloadPdf`)}
          </DownloadTreatmentPlanButton>
        </>
      )}
      {isTriagedOut && (
        <DownloadTreatmentPlanButton encounterId={encounterId} fileName="triaged-out-visit.pdf">
          {t(`${translationBaseNav}.savePdfTriageOut`)}
        </DownloadTreatmentPlanButton>
      )}
      {hasReferral && (
        <DownloadReferralLetterButton encounterId={encounterId} fileName="referral-letter.pdf">
          {t(`${translationBaseNav}.downloadReferralLetter`)}
        </DownloadReferralLetterButton>
      )}
    </div>
  );
}
